import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { LuCheck, LuPlus, LuX } from 'react-icons/lu';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PROGRAM_TYPE_COLORS } from '../constants';
import type { EnrollmentMode, ProgramOwner, ProgramTypeKey } from '../types';
import { useOrgAudienceOptions } from '../useOrgMemberAttributes';
import { useAppSelector } from '../../../../../../../redux/hooks';
import { useMemberWord } from '../../../../../../../utils/memberTypeLabels';
import useProfileDetails from '../../../../../../../hooks/useProfileDetails';
import { formatDate, fromEpochSeconds } from '../../../../../../../utils/formatDate';
import { AUDIENCE_MATCH_EXPLAINER, AudienceMatchToggle } from '../AudienceRulesEditor';
import type { AudienceMatchType } from '../../../../../utils/memberAttributes';
import { seededAudienceIds, type RecommendedCourse } from '../knowledgeGaps';
import type { AddProgramFormValues } from '../AddNewProgramDrawer';
import DosenInput from '../../../../../components/DosenInput';
// Shared with the AIMD Outcomes step so the chat enforces the same min-count and
// character limit as the standalone screen.
import { MIN_REQUIRED_OUTCOMES, OUTCOME_MAX_LENGTH } from '../../../../aimd-v2/constants';
// Tone options: the BE list is fetched at mount (same source the AIMD wizard
// uses); the static list stays as fallback so the step never renders empty.
import { DUMMY_TONE_OPTIONS } from '../../../../aimd-v2/constants';
// Chat-native document step — reuses the real AIMD upload APIs but with the
// chat's own UI (cards, inline URL form, reply-bar text entry). Needs a real
// groupId for uploads (created on entering Build with Elliott).
import type { DocumentsData, DocumentItem, ToneOption } from '../../../../aimd-v2/types';
import {
  createProgramGeneration,
  fetchOrgDocuments,
  fetchToneOptions,
  generatePreview,
  improvePreview,
  uploadFileDocument,
  uploadTextDocument,
  uploadUrlDocument,
  updateInstruction
} from '../../../../aimd-v2/api';
// ATC: the conversational phase (Requirements → Preview) runs in this component,
// before the embedded Structure/Content wizard mounts. The wizard carries its own
// heartbeat, so without one here the chat-phase design time never reaches the BE.
import { useAimdHeartbeat } from '../../../../aimd-v2/hooks/useAimdHeartbeat';
import { elliottTokens as t } from './tokens';
import ElliottMessage, { messageBodySx, AVATAR_GUTTER } from './ElliottMessage';
import LearnerMessage from './LearnerMessage';
import StreamingText from './StreamingText';
import TypingDots from './TypingDots';
import ChatInputBar from './ChatInputBar';
import { OptionPill, OptionCard } from './OptionPill';

const EMPTY: AddProgramFormValues = {
  name: '',
  programType: null,
  audience: [],
  audienceMatch: {},
  enrollmentMode: null,
  launchDate: null,
  ownerMode: null,
  existingOwnerId: null,
  inviteFirstName: '',
  inviteLastName: '',
  inviteEmail: ''
};

const CATEGORY_OPTIONS: Array<{ key: ProgramTypeKey; label: string; description: string }> = [
  { key: 'role-excellence', label: 'Role Excellence', description: 'Role-specific capability and enablement.' },
  { key: 'development', label: 'Development', description: 'Communication, collaboration, growth.' },
  { key: 'standards', label: 'Standards', description: 'Risk, compliance, governance, conduct, policy, process.' }
];

// Program format chosen after "Design Now" (distinct from Category above). Maps
// to the legacy creation routes for the eventual hand-off:
//   course → masterclass · guided → guided-masterclass · mentorship → mentorship
export type ProgramFormat = 'course' | 'mentorship' | 'guided';

// Built per render: the Mentorship card names mentors in the org's own word (Settings > Members).
const programTypeOptions = (
  w: ReturnType<typeof useMemberWord>
): Array<{ key: ProgramFormat; label: string; description: string }> => [
  {
    key: 'course',
    label: 'Course',
    description: 'Self-paced learning built from your inputs and internal documents.'
  },
  {
    key: 'mentorship',
    label: 'Mentorship',
    description: `A development path powered by internal ${w('mentor', { plural: true, lower: true })}.`
  },
  {
    key: 'guided',
    label: 'Guided Course',
    description: 'Self-paced course content blended with scheduled human touchpoints.'
  }
];

// Build method (Course / Guided only — Mentorship is single-path). Template is
// disabled for now ("Coming Soon").
export type BuildMethod = 'elliott' | 'manual' | 'template';

const BUILD_METHOD_OPTIONS: Array<{
  key: BuildMethod;
  label: string;
  description: string;
  badge?: string;
  comingSoon?: boolean;
}> = [
  {
    key: 'elliott',
    label: 'Build with Elliott',
    description: 'I build the program with you, step by step.'
  },
  {
    key: 'manual',
    label: 'Build Manually',
    description: 'Set up your modules and lessons yourself in the editor.'
  },
  {
    key: 'template',
    label: 'Start from a Template',
    description: 'Start from a ready-made program template.',
    badge: 'Coming Soon',
    comingSoon: true
  }
];

const EMAIL_RE = /\S+@\S+\.\S+/;

type Phase =
  // Menu entry (from the framework orb) + Invite Admin sub-flow (no stepper).
  | 'menu'
  | 'adminMode'
  | 'adminExisting'
  | 'adminFirst'
  | 'adminLast'
  | 'adminEmail'
  | 'adminDone'
  // Recommended Course review — every auto-filled input on one screen.
  | 'reviewRecommendation'
  | 'welcome'
  | 'pillar'
  | 'name'
  | 'category'
  | 'audience'
  | 'launchDate'
  | 'owner'
  | 'ownerExisting'
  | 'inviteFirst'
  | 'inviteLast'
  | 'inviteEmail'
  | 'design'
  | 'programType'
  | 'buildMethod'
  // Course/Guided · Build with Elliott sub-flow → hands off to AIMD Structure.
  | 'elliottPurpose'
  | 'elliottTone'
  | 'elliottOutcomes'
  | 'elliottDocs'
  | 'elliottPreview'
  // Mentorship sub-flow → creates the program, then Elliott confirms and the
  // modal returns to the framework (no Members step).
  | 'mentorObjectives'
  | 'mentorMatches'
  | 'mentorDone'
  // Build Manually → brief setup message while the group is created, then the
  // modal expands full-screen into the embedded Manual editor.
  | 'manualHandoff'
  | 'wip';

const FREE_TEXT_PHASES: Phase[] = [
  'name',
  'inviteFirst',
  'inviteLast',
  'inviteEmail',
  'adminFirst',
  'adminLast',
  'adminEmail',
  'elliottPurpose'
];

// Elliott's prompt for each phase. The welcome line is shown once up top; every
// other phase asks for one of the original Add New Program inputs, in order.
const PROMPTS: Record<Phase, string> = {
  menu: 'Hi, how can I help?',
  adminMode: "Great. Let's add an org admin. Are they already in your organization, or should I invite someone new?",
  adminExisting: 'Which person should I make an org admin?',
  adminFirst: "No problem, let's invite them. What's their first name?",
  adminLast: 'And their last name?',
  adminEmail: 'What email should I send the invite to?',
  adminDone: '', // set dynamically on confirmation
  reviewRecommendation: '', // set dynamically from the recommendation
  welcome:
    'Let’s build a new program. We can build and publish the program now, or save a draft and finish later. First, a few quick details.',
  pillar: 'Which stage should this program sit in?',
  name: 'Firstly, what would you like to name your program?',
  category: 'What category does this program fall under?',
  audience: 'Who is this program for? Everyone who matches is enrolled automatically, including people who join later.',
  launchDate: 'Do you have a publish-by date in mind?',
  owner: "Who'll own this program?",
  ownerExisting: 'Great. Which person should it be?',
  inviteFirst: "No problem, let's invite them. What's their first name?",
  inviteLast: 'And their last name?',
  inviteEmail: 'Finally, what’s their email address?',
  design: 'That’s everything I need. Would you like to build the program now, or come back later?',
  programType: "Let's design it. First, what type of program is this?",
  buildMethod: 'And how would you like to build it?',
  elliottPurpose:
    'What’s the purpose of this program? A few sentences on the problem to solve and the topics to cover is ideal.',
  elliottTone: 'What tone should the content take? Pick up to 3.',
  elliottOutcomes:
    'By the end of this program, what should learners be able to do? Aim for three to five goals a manager could see on the job.',
  elliottDocs: "What knowledge should I use to ground the program's content?",
  elliottPreview:
    "Brilliant, that's everything I need. Give me a moment while I put together a preview of your program...",
  mentorObjectives: '', // set dynamically with the org's member words
  mentorMatches: "A couple of match settings and we're ready to build your program.",
  mentorDone:
    "Great, that's all I need to build your program. In a moment you'll see it on the Dashboard, and you can add matches whenever you're ready.",
  manualHandoff: 'Setting up your manual workspace...',
  // Temporary boundary — the Manual / Mentorship flows are wired in later milestones.
  wip: "Perfect, that's everything captured. Let's get building..."
};

// Maps each conversation phase to its top-nav stepper index. The stepper UI
// itself lives in Stepper.tsx and is rendered by the modal shell (so it
// persists when the embedded Structure/Content wizard takes over).
const STEP_FOR_PHASE: Record<Phase, number> = {
  // Menu + Invite Admin have no stepper (index < 0 hides it in the modal shell).
  menu: -1,
  adminMode: -1,
  adminExisting: -1,
  adminFirst: -1,
  adminLast: -1,
  adminEmail: -1,
  adminDone: -1,
  reviewRecommendation: -1,
  // Requirements (0) covers the core fields + purpose + tone.
  welcome: 0,
  pillar: 0,
  name: 0,
  category: 0,
  audience: 0,
  launchDate: 0,
  owner: 0,
  ownerExisting: 0,
  inviteFirst: 0,
  inviteLast: 0,
  inviteEmail: 0,
  design: 0,
  programType: 0,
  buildMethod: 0,
  elliottPurpose: 0,
  elliottTone: 0,
  elliottOutcomes: 1, // Outcomes
  elliottDocs: 2, // Documents
  elliottPreview: 3, // Preview
  // Mentorship has its own short flow — hide the course stepper.
  mentorObjectives: -1,
  mentorMatches: -1,
  mentorDone: -1,
  manualHandoff: -1,
  wip: 3
};

type Turn = { id: string; role: 'elliott' | 'learner'; text: string };

// Data gathered conversationally for the Course/Guided · Build with Elliott
// path, handed to the dashboard which persists it (AIMD buildState) and routes
// the admin to the existing Structure step.
export interface ElliottBuildPayload {
  programFormat: ProgramFormat; // 'course' | 'guided'
  groupId: string | null; // group is created on entering the Elliott flow
  // Generation record created for the preview step — seeded into the wizard
  // so it reuses this id instead of spawning a duplicate generation.
  generationId: string | null;
  programName: string;
  audience: string[];
  description: string;
  tone: string[];
  outcomes: string[];
  selectedDocIds: string[];
  preview: string; // BE-generated preview (or local fallback on API failure)
}

// A single document row — select checkbox, title, type chip, optional inline
// instructions, and (for new uploads) a remove control.
const DocRow: React.FC<{
  doc: DocumentItem;
  onToggle: () => void;
  onInstruction: (v: string) => void;
  onRemove?: () => void;
}> = ({ doc, onToggle, onInstruction, onRemove }) => {
  const [open, setOpen] = useState(Boolean(doc.instructions));
  const [instr, setInstr] = useState(doc.instructions || '');
  // A document can only be selected once it has finished indexing. Unknown
  // (null) status is treated as ready so legacy docs aren't hidden. Unticking
  // is always allowed: uploads arrive pre-selected while still indexing, and
  // the admin must be able to drop one rather than wait on it.
  const failed = doc.processingStatus === 'failed';
  const notReady = doc.processingStatus === 'processing' || doc.processingStatus === 'uploaded' || failed;
  const canToggle = !notReady || doc.selected;
  return (
    <Box
      sx={{
        border: `1px solid ${doc.selected ? t.brandLight : t.borderContrast}`,
        borderRadius: '6px',
        bgcolor: t.surface,
        px: '12px',
        py: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          onClick={() => canToggle && onToggle()}
          sx={{
            flexShrink: 0,
            width: 18,
            height: 18,
            borderRadius: '4px',
            cursor: canToggle ? 'pointer' : 'default',
            opacity: canToggle ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: doc.selected ? '1.5px solid #f8de89' : `1.5px solid ${t.borderContrast}`,
            background: doc.selected ? t.brandyFill : 'transparent'
          }}
        >
          {doc.selected && <LuCheck size={12} color={t.textPrimary} strokeWidth={3} />}
        </Box>
        <Typography
          sx={{ flex: 1, minWidth: 0, fontSize: 13, color: notReady ? t.textTertiary : t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {doc.title || 'Untitled'}
        </Typography>
        {/* Not-ready docs can't be selected yet — show why. */}
        {notReady && (
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: failed ? t.error : t.brandLight,
              border: `1px solid ${failed ? t.error : t.brandLight}`,
              borderRadius: '4px',
              px: '5px',
              py: '1px'
            }}
          >
            {failed ? 'Failed' : 'Indexing'}
          </Typography>
        )}
        <Typography
          sx={{ flexShrink: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: t.textTertiary, border: `1px solid ${t.borderContrast}`, borderRadius: '4px', px: '5px', py: '1px' }}
        >
          {doc.fileTypeLabel}
        </Typography>
        <Box
          onClick={() => setOpen((o) => !o)}
          title="Add instructions"
          sx={{ flexShrink: 0, display: 'flex', cursor: 'pointer', color: open ? t.brandLight : t.textTertiary }}
        >
          <LuPlus size={16} />
        </Box>
        {onRemove && (
          <Box onClick={onRemove} title="Remove" sx={{ flexShrink: 0, display: 'flex', cursor: 'pointer', color: t.textTertiary, '&:hover': { color: t.error } }}>
            <LuX size={16} />
          </Box>
        )}
      </Box>
      <Collapse in={open}>
        <TextField
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          onBlur={() => onInstruction(instr)}
          placeholder="How should I use this document? For example, focus on Section 1."
          size="small"
          fullWidth
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(0,0,0,0.4)',
              borderRadius: '6px',
              color: t.textPrimary,
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              '& fieldset': { borderColor: t.borderContrast },
              '&:hover fieldset': { borderColor: t.textTertiary },
              '&.Mui-focused fieldset': { borderColor: t.brandLight }
            },
            '& .MuiOutlinedInput-input::placeholder': { color: t.textTertiary, opacity: 1 }
          }}
        />
      </Collapse>
    </Box>
  );
};

interface Props {
  existingMembers?: ProgramOwner[];
  // Existing program/group names — used to validate the chosen name in chat.
  existingProgramNames?: string[];
  // 'menu' opens with Elliott offering the framework actions; 'program' starts
  // the Add New Program flow directly (per-pillar CTA).
  entry?: 'program' | 'menu';
  // Org display name (for menu copy) + whether the viewer is an Org Admin
  // (gates the Edit Framework menu action).
  orgName?: string;
  isOrgAdmin?: boolean;
  // Learning pillars — when Add New Program is opened from the chat (no pillar
  // pre-selected), Elliott asks which pillar before the name.
  pillars?: { id: string; name: string }[];
  onPillarSelect?: (pillarId: string) => void;
  // Menu actions that leave the chat: close the modal and run the dashboard action.
  onEditFramework?: () => void;
  // Build my Learning Framework — opens the recommendation builder. Only
  // offered when the dashboard passes it (org admins).
  onBuildFramework?: () => void;
  // Whether the org already has a saved framework (BE / local store): the menu
  // action then reads "See" rather than "Build".
  frameworkBuilt?: boolean;
  // Setup Guide — progress shown on the menu option, and opening the guide.
  setupProgress?: { done: number; total: number };
  onOpenSetupGuide?: () => void;
  // Sync Knowledge: returns whether a knowledge integration (SharePoint) exists
  // for the org — and triggers a sync if so. False → Elliott nudges to connect.
  onSyncKnowledge?: () => Promise<boolean>;
  // True once the org has a knowledge base connected — the menu then drops the
  // Sync Knowledge action entirely (nothing left to connect).
  hasKnowledgeIntegration?: boolean;
  // Opened from a Recommended Course card — name/audience/purpose/tone are
  // already known, so the conversation starts past them.
  recommendedSeed?: RecommendedCourse | null;
  // Invite Admin sub-flow: sends the org-scoped admin invitation. Resolves true
  // on success — Elliott confirms or surfaces the failure in-chat.
  onInviteAdmin?: (data: { firstName: string; lastName: string; email: string }) => Promise<boolean>;
  // Invite Org Admin → "Choose Existing Member": promote that member to org
  // admin. Resolves true on success.
  onPromoteOrgAdmin?: (userId: string) => Promise<boolean>;
  // Invite Org Admin is only offered to org (and platform) admins.
  canInviteOrgAdmin?: boolean;
  onSubmit: (values: AddProgramFormValues, intent: 'design-now' | 'design-later') => void | Promise<void>;
  // Create the group up front (on entering Build with Elliott) so the Documents
  // step has a real groupId to upload against. Returns the new group id.
  onCreateGroup?: (values: AddProgramFormValues) => Promise<string | null>;
  // Recommended Course review — the group is created when the category is
  // picked, so audience edits made after that are saved before Structure.
  onSaveAudience?: (groupId: string, values: AddProgramFormValues) => Promise<boolean>;
  // Reports the current top-nav stepper index (0–4) as the conversation advances,
  // so the modal shell can render the shared stepper.
  onStepChange?: (index: number) => void;
  // Course/Guided · Build with Elliott — hand the collected inputs to the modal,
  // which seeds the AIMD wizard and switches to the embedded Structure/Content view.
  onEnterStructure?: (payload: ElliottBuildPayload) => void;
  // Course/Guided · Build Manually — the modal seeds ProgramCreation state and
  // switches to the embedded Manual builder.
  onEnterManual?: (payload: { groupId: string; programFormat: ProgramFormat; programName: string }) => void;
  // Mentorship — hand the captured objectives + match settings to the modal,
  // which creates the group, saves them, and switches to the embedded Members step.
  onEnterMentorship?: (payload: MentorshipPayload) => void;
  // Capability release: a Course or Guided Course built with Elliott is built
  // in the Publish Training flow, not in this conversation. When given, the
  // conversation asks the program type and how to build it FIRST and hands that
  // build over (so nothing is asked here that the flow asks again); it carries
  // on with the program's details only for a manual build or a Mentorship.
  onBuildWithElliott?: (format: ProgramFormat) => void;
}

export interface MentorshipPayload {
  programName: string;
  // LL program type (role-excellence / development / standards), captured in the
  // 'category' phase — the group's mentorship *model* is set separately.
  programType: ProgramTypeKey;
  audience: string[];
  // Any/All per attribute list, keyed by list id.
  audienceMatch: Record<string, AudienceMatchType>;
  enrollmentMode: EnrollmentMode | null;
  objectives: string[];
  maxMatchesPerMentee: number;
  maxMatchesPerMentor: number;
  sessionsPerMatch: number;
  // The launch date and owner answered earlier in the chat (an owner invited by
  // e-mail is invited once the program exists), as the other paths send them.
  launchDate: AddProgramFormValues['launchDate'];
  ownerMode: AddProgramFormValues['ownerMode'];
  existingOwnerId: AddProgramFormValues['existingOwnerId'];
  inviteFirstName: string;
  inviteLastName: string;
  inviteEmail: string;
}

// Local fallback when generatePreview / improvePreview fail — keeps the
// conversation moving instead of dead-ending on an API error.
const buildFallbackPreview = (
  name: string,
  description: string,
  outcomes: string[],
  feedback?: string
): string => {
  const title = name.trim() || 'Your program';
  const focus = description.trim() || 'the topics you outlined';
  const oc = outcomes.length
    ? ` By the end, learners will be able to: ${outcomes.slice(0, 3).join('; ')}.`
    : '';
  const fb = feedback && feedback.trim() ? ` (Refined to reflect your feedback: ${feedback.trim()}.)` : '';
  return `${title} is a structured, self-paced learning experience focused on ${focus}. It blends concise explanations with practical, role-relevant examples so learners can apply what they learn straight away.${oc}${fb}`;
};

// The generated preview separates sections with single newlines; one pre-wrapped
// Typography shows them with no spacing, so it reads as a wall of text. Render
// each line as its own paragraph with breathing room instead.
const ParagraphText: React.FC<{ text: string; sx?: object }> = ({ text, sx }) => {
  const paragraphs = text
    .split('\n')
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {paragraphs.map((p, i) => (
        <Typography key={i} sx={{ ...messageBodySx, ...sx }}>
          {p}
        </Typography>
      ))}
    </Box>
  );
};

// One labelled row on the Recommended Course review screen.
const ReviewField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: t.brandLight
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const reviewInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(0,0,0,0.4)',
    borderRadius: '6px',
    color: t.textPrimary,
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    '& fieldset': { borderColor: t.borderContrast },
    '&:hover fieldset': { borderColor: t.textTertiary },
    '&.Mui-focused fieldset': { borderColor: t.brandLight }
  }
};

const ProgramChat: React.FC<Props> = ({
  existingMembers = [],
  existingProgramNames = [],
  entry = 'program',
  orgName,
  isOrgAdmin = true,
  pillars = [],
  onPillarSelect,
  onEditFramework,
  onBuildFramework,
  frameworkBuilt = false,
  setupProgress,
  onOpenSetupGuide,
  onSyncKnowledge,
  hasKnowledgeIntegration = false,
  recommendedSeed = null,
  onInviteAdmin,
  onPromoteOrgAdmin,
  canInviteOrgAdmin = false,
  onSubmit,
  onCreateGroup,
  onSaveAudience,
  onStepChange,
  onEnterStructure,
  onEnterManual,
  onEnterMentorship,
  onBuildWithElliott
}) => {
  const { options: audienceOptions, catalog: audienceCatalog } = useOrgAudienceOptions();
  const w = useMemberWord();
  const mentorsWord = w('mentor', { plural: true, lower: true });
  const menteesWord = w('mentee', { plural: true, lower: true });
  // "Me" owner option: owner ids are Mongo user ids, as is the signed-in profile's _id.
  const myUserId = useAppSelector((state) => state.userProfile.data?._id);
  const me = myUserId ? existingMembers.find((m) => m.id === String(myUserId)) : undefined;

  // A Recommended Course arrives with its name and audience already decided, so
  // the conversation opens on the category step rather than asking for them
  // again. The audience comes as the org's value ids — exact, where the same
  // label can sit in two lists; gaps from before ids were kept only carry
  // labels, which are resolved back by name. A capability recommendation is
  // for a role, which is not an attribute value: it opens with no audience.
  const seededValues = (): AddProgramFormValues => {
    if (!recommendedSeed) return EMPTY;
    return { ...EMPTY, name: recommendedSeed.name, audience: seededAudienceIds(recommendedSeed, audienceOptions) };
  };

  const [values, setValues] = useState<AddProgramFormValues>(seededValues);
  // The org's attribute options load after mount, so the initializer above
  // can't resolve the recommendation's audience labels yet — do it again once
  // they land, unless the admin has already picked an audience by hand.
  useEffect(() => {
    if (!recommendedSeed || audienceOptions.length === 0) return;
    const { audience } = seededValues();
    if (audience.length === 0) return;
    setValues((prev) => (prev.audience.length > 0 ? prev : { ...prev, audience }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceOptions.length]);
  const [thread, setThread] = useState<Turn[]>(() =>
    recommendedSeed
      ? [
          {
            id: 'seed-recap',
            role: 'elliott',
            text: `Let's publish "${
              recommendedSeed.name
            }", the course I recommended for ${recommendedSeed.audiences.join(', ')}. ${
              recommendedSeed.audienceIsRoles
                ? "I've carried over the name and purpose; choose who it's for and check the rest with me."
                : "I've carried over the name, audience and purpose; just check the rest with me."
            }`
          }
        ]
      : []
  );
  const [phase, setPhase] = useState<Phase>(
    entry === 'menu' ? 'menu' : recommendedSeed ? 'reviewRecommendation' : 'welcome'
  );
  // Invite Admin sub-flow fields.
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');

  // Marks that the admin has opened the Elliott menu. The greeting is the same
  // on every visit now ("Hi {firstName}, how can I help?").
  useEffect(() => {
    if (entry === 'menu') localStorage.setItem('elliott_copilot_seen', '1');
  }, [entry]);

  // Org copy helpers.
  const orgLabel = orgName?.trim() || 'your organization';
  const orgPoss = `${orgLabel}'s`;
  const { firstName } = useProfileDetails();
  const menuGreeting = firstName ? `Hi ${firstName}, how can I help?` : PROMPTS.menu;
  const [typing, setTyping] = useState(true); // typing dots before a prompt streams
  const [promptStreamed, setPromptStreamed] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState<'design-now' | 'design-later' | null>(null);
  // When set, the active Elliott message streams this instead of PROMPTS[phase]
  // (used to re-ask in place, e.g. when a chosen program name is already taken).
  const [promptOverride, setPromptOverride] = useState<string | null>(null);
  // Program format chosen after Design Now (course / mentorship / guided).
  const [programFormat, setProgramFormat] = useState<ProgramFormat | null>(null);
  // Build method chosen for Course / Guided (elliott / manual / template).
  const [buildMethod, setBuildMethod] = useState<BuildMethod | null>(null);
  // Mentorship sub-flow: objectives + match settings.
  const [mentorObjectives, setMentorObjectives] = useState<string[]>([]);
  const [matchMentee, setMatchMentee] = useState(1);
  const [matchMentor, setMatchMentor] = useState(1);
  const [sessionsPerMatch, setSessionsPerMatch] = useState(1);
  // Build with Elliott sub-flow data. A Recommended Course brings its own
  // purpose and tone across, so the Elliott build path opens with them filled.
  const [purpose, setPurpose] = useState(recommendedSeed?.purpose ?? '');
  const [tone, setTone] = useState<string[]>(recommendedSeed?.tone ?? []);
  // Canonical tone list from the BE; falls back to the static AIMD list.
  const [toneOptions, setToneOptions] = useState<ToneOption[]>(DUMMY_TONE_OPTIONS);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  // Real AIMD document state (uploaded + previously-available docs).
  const [documents, setDocuments] = useState<DocumentsData>({
    uploadedDocuments: [],
    previousDocuments: []
  });
  // Group created when the admin enters Build with Elliott, so Documents can
  // upload against it (and the hand-off doesn't re-create it).
  const [groupId, setGroupId] = useState<string | null>(null);
  // ATC: count active design time across the chat phase. No-ops until the group
  // exists (Build with Elliott / onCreateGroup) and the BE clamps + stops
  // counting once the program reaches Invited. When the flow hands off to the
  // embedded wizard, this component unmounts and the wizard's own heartbeat
  // takes over — the two never run at the same time, so no double-counting.
  useAimdHeartbeat(groupId);
  // Generation record for the preview step — created lazily (attached to the
  // drawer-created group) and handed to the wizard so it reuses the same id.
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  // Latest preview feedback + the preview text it applies to (improvePreview
  // needs the preview being replaced).
  const [lastFeedback, setLastFeedback] = useState('');
  const previousPreviewRef = useRef<string | null>(null);

  const selectedDocIds = [...documents.uploadedDocuments, ...documents.previousDocuments]
    .filter((d) => d.selected)
    .map((d) => d.id);
  // Selected documents Elliott can't read: still indexing, or indexing failed.
  // Continue waits for them — advancing with one ticked would silently produce
  // a program that ignores it. Uploads arrive pre-selected, so a failed upload
  // stays ticked until the admin unticks it.
  const isIndexing = (d: DocumentItem) => d.processingStatus === 'processing' || d.processingStatus === 'uploaded';
  const isFailed = (d: DocumentItem) => d.processingStatus === 'failed';
  const listedDocs = [...documents.uploadedDocuments, ...documents.previousDocuments];
  const indexingSelectedCount = listedDocs.filter((d) => d.selected && isIndexing(d)).length;
  const failedSelectedCount = listedDocs.filter((d) => d.selected && isFailed(d)).length;
  const blockedSelectedCount = indexingSelectedCount + failedSelectedCount;
  const hasIndexingDocs = listedDocs.some(isIndexing);

  // Chat-native document step state.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<{ id: string; name: string }[]>([]);
  const [textMode, setTextMode] = useState<'idle' | 'title' | 'body'>('idle');
  const [textTitle, setTextTitle] = useState('');
  const [urlMode, setUrlMode] = useState(false);
  const [urlTitle, setUrlTitle] = useState('');
  const [urlValue, setUrlValue] = useState('');
  const [prevExpanded, setPrevExpanded] = useState(false);
  const [prevLoaded, setPrevLoaded] = useState(false);

  const update = <K extends keyof AddProgramFormValues>(key: K, val: AddProgramFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  // Fetch the canonical tone list once; keep the static fallback on failure.
  useEffect(() => {
    let cancelled = false;
    fetchToneOptions()
      .then((tones) => {
        if (!cancelled && tones.length > 0) setToneOptions(tones);
      })
      .catch(() => {
        // fallback list already in state
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Each new phase shows a brief "typing" beat, then Elliott's prompt streams in.
  useEffect(() => {
    setPromptStreamed(false);
    setTyping(true);
    const id = window.setTimeout(() => setTyping(false), 450);
    return () => window.clearTimeout(id);
  }, [phase]);

  // Report the current stepper index up to the modal shell (which owns the stepper).
  useEffect(() => {
    onStepChange?.(STEP_FOR_PHASE[phase]);
  }, [phase, onStepChange]);

  // Outcomes / Objectives are captured as discrete entries (not a paragraph).
  // Seed the minimum number of empty fields when the admin first reaches each
  // step so they always see at least three to fill in.
  useEffect(() => {
    if (phase === 'elliottOutcomes') {
      setOutcomes((prev) =>
        prev.length >= MIN_REQUIRED_OUTCOMES
          ? prev
          : [...prev, ...Array(MIN_REQUIRED_OUTCOMES - prev.length).fill('')]
      );
    }
    if (phase === 'mentorObjectives') {
      setMentorObjectives((prev) =>
        prev.length >= MIN_REQUIRED_OUTCOMES
          ? prev
          : [...prev, ...Array(MIN_REQUIRED_OUTCOMES - prev.length).fill('')]
      );
    }
  }, [phase]);

  // The type and build method are asked first where a course built with Elliott leaves for Publish Training.
  const typeFirst = Boolean(onBuildWithElliott);
  // Where the program's own details begin: its pillar (from the menu, where none was chosen), then its name.
  const detailsStart: Phase = entry === 'menu' && pillars.length > 0 ? 'pillar' : 'name';
  const activePromptText =
    promptOverride ??
    (phase === 'menu'
      ? menuGreeting
      : typeFirst && phase === 'welcome'
      ? 'Brilliant, a brand new program! Let’s start with what you are building.'
      : typeFirst && phase === 'programType'
      ? 'What type of program is this?'
      : phase === 'reviewRecommendation'
      ? `Here's everything I'd use to build "${
          recommendedSeed?.name ?? 'this program'
        }". Change anything you disagree with, then I'll build the structure.`
      : phase === 'mentorObjectives'
      ? `What should ${mentorsWord} and ${menteesWord} achieve together? Add at least three objectives.`
      : PROMPTS[phase]);

  // Commit the current prompt + the admin's answer to history, then advance.
  const commitAndAdvance = (answer: string, next: Phase) => {
    setThread((prev) => [
      ...prev,
      { id: `${phase}-q-${prev.length}`, role: 'elliott', text: activePromptText },
      { id: `${phase}-a-${prev.length}`, role: 'learner', text: answer }
    ]);
    setDraft('');
    setPromptOverride(null);
    setPhase(next);
  };

  // Re-ask within the current phase (no advance): commit the current Q + the
  // admin's answer to history, then stream a new prompt in place.
  const reAsk = (answer: string, overrideMsg: string) => {
    setThread((prev) => [
      ...prev,
      { id: `${phase}-q-${prev.length}`, role: 'elliott', text: activePromptText },
      { id: `${phase}-a-${prev.length}`, role: 'learner', text: answer }
    ]);
    setDraft('');
    setPromptOverride(overrideMsg);
    setPromptStreamed(false);
    setTyping(true);
    window.setTimeout(() => setTyping(false), 450);
  };

  // Menu · Sync Knowledge — check for an org knowledge integration, then either
  // confirm the sync or nudge the admin to connect one first. Stays on the menu.
  const handleSyncKnowledge = async () => {
    setThread((prev) => [
      ...prev,
      { id: `menu-q-${prev.length}`, role: 'elliott', text: activePromptText },
      { id: `menu-a-${prev.length}`, role: 'learner', text: 'Sync Knowledge' }
    ]);
    setDraft('');
    setPromptOverride(null);
    setPromptStreamed(false);
    setTyping(true);
    let connected = false;
    try {
      connected = (await onSyncKnowledge?.()) ?? false;
    } catch {
      connected = false;
    }
    setPromptOverride(
      connected
        ? 'I’m syncing with your connected Knowledge now, so I always work from your latest documents.'
        : "It looks like you haven't connected a knowledge source yet. Head to your Knowledge to connect SharePoint first. Alternatively, we can do something else right now if you'd prefer."
    );
    setTyping(false);
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    switch (phase) {
      case 'name': {
        const taken = existingProgramNames.some(
          (n) => n.trim().toLowerCase() === text.toLowerCase()
        );
        if (taken) {
          reAsk(
            text,
            `Hmm, a program called “${text}” already exists. Could you give this one a different name?`
          );
          return;
        }
        update('name', text);
        commitAndAdvance(text, 'category');
        break;
      }
      case 'inviteFirst':
        update('inviteFirstName', text);
        commitAndAdvance(text, 'inviteLast');
        break;
      case 'inviteLast':
        update('inviteLastName', text);
        commitAndAdvance(text, 'inviteEmail');
        break;
      case 'inviteEmail':
        if (!EMAIL_RE.test(text)) return;
        update('inviteEmail', text);
        commitAndAdvance(text, 'design');
        break;
      case 'adminFirst':
        setAdminFirst(text);
        commitAndAdvance(text, 'adminLast');
        break;
      case 'adminLast':
        setAdminLast(text);
        commitAndAdvance(text, 'adminEmail');
        break;
      case 'adminEmail': {
        if (!EMAIL_RE.test(text)) return;
        setThread((prev) => [
          ...prev,
          { id: `adminEmail-q-${prev.length}`, role: 'elliott', text: activePromptText },
          { id: `adminEmail-a-${prev.length}`, role: 'learner', text }
        ]);
        setDraft('');
        setPromptOverride(`Sending an org admin invite to ${text}...`);
        setPhase('adminDone');
        // Fire the org-scoped invite; rewrite the confirmation once it resolves.
        void (async () => {
          const ok =
            (await onInviteAdmin?.({ firstName: adminFirst, lastName: adminLast, email: text })) ??
            false;
          setPromptOverride(
            ok
              ? `Done. I've sent an org admin invite to ${text}${
                  adminFirst ? ` for ${adminFirst}${adminLast ? ` ${adminLast}` : ''}` : ''
                }. They'll get access once they accept.`
              : `I couldn't send the invite to ${text}. Please check the address, or invite them from People.`
          );
        })();
        break;
      }
      case 'elliottPurpose':
        setPurpose(text);
        commitAndAdvance(text, 'elliottTone');
        break;
      case 'elliottPreview': {
        // Feedback to refine the preview: archive the current preview + the
        // feedback, then regenerate (DUMMY) incorporating it.
        if (preview === null || generatingPreview || handingOff) return;
        setThread((prev) => [
          ...prev,
          { id: `preview-${prev.length}`, role: 'elliott', text: preview },
          { id: `preview-fb-${prev.length}`, role: 'learner', text }
        ]);
        previousPreviewRef.current = preview;
        setLastFeedback(text);
        setDraft('');
        setPreview(null);
        setPromptOverride('Good feedback. Let me revise the preview...');
        setPromptStreamed(false);
        setTyping(true);
        window.setTimeout(() => setTyping(false), 450);
        break;
      }
      case 'elliottDocs':
        // Reply bar is repurposed for the "Upload Text" flow: title, then body.
        if (textMode === 'title') {
          setTextTitle(text);
          setTextMode('body');
          setDraft('');
        } else if (textMode === 'body') {
          submitText(text);
          setDraft('');
        }
        break;
      default:
        break;
    }
  };

  // Recommended Course review — create the group and generate the preview in the
  // background so the admin lands on a screen that's already complete. Mirrors
  // what the Build with Elliott path does on entry; runs once.
  const reviewPrepStarted = useRef(false);
  useEffect(() => {
    if (phase !== 'reviewRecommendation' || reviewPrepStarted.current) return;
    if (!recommendedSeed || !values.name.trim() || !values.programType) return;
    reviewPrepStarted.current = true;
    setProgramFormat('course');
    void (async () => {
      try {
        const id = await onCreateGroup?.(values);
        if (id) setGroupId(id);
      } catch (e) {
        console.error('[Elliott] group creation for recommendation failed', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, recommendedSeed, values.name, values.programType]);

  // Once the group exists, ask the BE for a preview of the recommended program.
  useEffect(() => {
    if (phase !== 'reviewRecommendation' || !groupId || preview || generatingPreview) return;
    startPreviewGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, groupId]);

  // Build with Elliott · hand the collected inputs to the modal, which seeds the
  // AIMD wizard and switches to the embedded Structure/Content view (no redirect).
  const handleElliottHandoff = async () => {
    if (handingOff || !programFormat) return;
    setHandingOff(true);
    // Recommended Course: the group was created when the category was picked,
    // so save the reviewed audience (values + Any/All) before Structure.
    if (recommendedSeed && groupId && onSaveAudience) {
      const saved = await onSaveAudience(groupId, values);
      if (!saved) {
        setHandingOff(false);
        return;
      }
    }
    onEnterStructure?.({
      programFormat,
      groupId,
      generationId,
      programName: values.name,
      audience: values.audience,
      description: purpose,
      tone,
      outcomes,
      selectedDocIds,
      preview: preview ?? ''
    });
  };

  // Mentorship · hand the captured objectives + match settings to the modal,
  // which creates + persists the program. Elliott then confirms (mentorDone) and
  // the modal returns to the framework — there is no Members step.
  const handleEnterMentorship = () => {
    if (!values.programType || handingOff) return;
    setHandingOff(true);
    commitAndAdvance('Build Program', 'mentorDone');
    onEnterMentorship?.({
      programName: values.name,
      programType: values.programType,
      audience: values.audience,
      audienceMatch: values.audienceMatch,
      enrollmentMode: values.enrollmentMode,
      objectives: mentorObjectives,
      maxMatchesPerMentee: matchMentee,
      maxMatchesPerMentor: matchMentor,
      sessionsPerMatch,
      launchDate: values.launchDate,
      ownerMode: values.ownerMode,
      existingOwnerId: values.existingOwnerId,
      inviteFirstName: values.inviteFirstName,
      inviteLastName: values.inviteLastName,
      inviteEmail: values.inviteEmail
    });
  };

  // Build with Elliott: generate (or refine, when feedback is given) the program
  // preview via the BE. Triggered from the preview prompt's onDone and when the
  // admin submits feedback. Falls back to a locally-built preview on API failure
  // so the conversation never dead-ends.
  const startPreviewGeneration = (feedback?: string) => {
    setGeneratingPreview(true);
    void (async () => {
      try {
        let genId = generationId;
        if (!genId) {
          if (!groupId) throw new Error('group not ready for preview generation');
          // Attach the generation to the drawer-created group — the wizard
          // reuses this id, so no duplicate generation record is spawned.
          const created = await createProgramGeneration(groupId);
          genId = created.generationId;
          setGenerationId(genId);
        }
        const payload = {
          generalPurpose: purpose,
          learningObjectives: outcomes,
          programName: values.name
        };
        const result =
          feedback && previousPreviewRef.current
            ? await improvePreview(genId, {
                ...payload,
                currentPreview: previousPreviewRef.current,
                feedback
              })
            : await generatePreview(genId, payload);
        setPreview(
          result.preview.trim() || buildFallbackPreview(values.name, purpose, outcomes, feedback)
        );
      } catch (e) {
        console.error('[Elliott] preview generation failed', e);
        setPreview(buildFallbackPreview(values.name, purpose, outcomes, feedback));
      } finally {
        setGeneratingPreview(false);
      }
    })();
  };

  // ── Documents: chat-native upload helpers (reuse the real AIMD upload APIs) ──
  const addUploadedDoc = (item: DocumentItem) =>
    setDocuments((prev) => ({ ...prev, uploadedDocuments: [...prev.uploadedDocuments, item] }));
  const dropPending = (tempId: string) =>
    setPendingFiles((prev) => prev.filter((p) => p.id !== tempId));

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!groupId) return;
    files.forEach((file) => {
      const tempId = `file-${Date.now()}-${file.name}`;
      setPendingFiles((prev) => [...prev, { id: tempId, name: file.name }]);
      uploadFileDocument(file, groupId)
        .then((item) => {
          dropPending(tempId);
          addUploadedDoc(item);
        })
        .catch(() => dropPending(tempId));
    });
  };

  const submitUrl = () => {
    if (!urlValue.trim() || !groupId) return;
    const title = urlTitle.trim() || urlValue.trim();
    const tempId = `url-${Date.now()}`;
    setPendingFiles((prev) => [...prev, { id: tempId, name: title }]);
    setUrlMode(false);
    setUrlTitle('');
    setUrlValue('');
    uploadUrlDocument(title, urlValue.trim(), groupId)
      .then((item) => {
        dropPending(tempId);
        addUploadedDoc(item);
      })
      .catch(() => dropPending(tempId));
  };

  const submitText = (body: string) => {
    const title = textTitle.trim() || 'Pasted text';
    setTextMode('idle');
    setTextTitle('');
    if (!groupId) return;
    const tempId = `text-${Date.now()}`;
    setPendingFiles((prev) => [...prev, { id: tempId, name: title }]);
    uploadTextDocument(title, body, groupId)
      .then((item) => {
        dropPending(tempId);
        addUploadedDoc(item);
      })
      .catch(() => dropPending(tempId));
  };

  const toggleDoc = (id: string) =>
    setDocuments((prev) => ({
      uploadedDocuments: prev.uploadedDocuments.map((d) => (d.id === id ? { ...d, selected: !d.selected } : d)),
      previousDocuments: prev.previousDocuments.map((d) => (d.id === id ? { ...d, selected: !d.selected } : d))
    }));

  const removeUploadedDoc = (id: string) =>
    setDocuments((prev) => ({ ...prev, uploadedDocuments: prev.uploadedDocuments.filter((d) => d.id !== id) }));

  const setDocInstruction = (id: string, instruction: string) => {
    setDocuments((prev) => ({
      uploadedDocuments: prev.uploadedDocuments.map((d) => (d.id === id ? { ...d, instructions: instruction } : d)),
      previousDocuments: prev.previousDocuments.map((d) => (d.id === id ? { ...d, instructions: instruction } : d))
    }));
    // Best-effort persist; ignored if BE not reachable in dummy mode.
    updateInstruction(id, instruction).catch(() => {});
  };

  // Load previously-uploaded org docs once we reach the document step.
  useEffect(() => {
    if (phase !== 'elliottDocs' || !groupId || prevLoaded) return;
    setPrevLoaded(true);
    fetchOrgDocuments(groupId)
      .then((docs) => {
        // Dedupe by id — the org-docs endpoint can return the same doc once per
        // saved instruction, which would list it multiple times. Show each doc
        // once with a blank instruction (the admin can add their own).
        const seen = new Set<string>();
        const unique = docs
          .filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)))
          .map((d) => ({ ...d, selected: false, instructions: '' }));
        setDocuments((prev) => ({ ...prev, previousDocuments: unique }));
      })
      .catch(() => {});
  }, [phase, groupId, prevLoaded]);

  // While any listed document is still indexing, poll the org-docs endpoint so
  // the "Indexing" badge clears and Continue unlocks without a page refresh.
  // Only the processing status is refreshed; selection and instructions are
  // local state and survive the poll.
  useEffect(() => {
    if (!hasIndexingDocs || !groupId) return undefined;
    const refreshStatuses = () =>
      fetchOrgDocuments(groupId)
        .then((docs) => {
          const statusById = new Map(docs.map((d) => [d.id, d.processingStatus]));
          const refresh = (list: DocumentItem[]) =>
            list.map((d) => (statusById.has(d.id) ? { ...d, processingStatus: statusById.get(d.id) ?? null } : d));
          setDocuments((prev) => ({
            ...prev,
            uploadedDocuments: refresh(prev.uploadedDocuments),
            previousDocuments: refresh(prev.previousDocuments)
          }));
        })
        .catch(() => {});
    const interval = setInterval(refreshStatuses, 5000);
    return () => clearInterval(interval);
  }, [hasIndexingDocs, groupId]);

  // Build Manually: a brief setup message (no stepper) while the group is created, then the
  // hand-off, where the modal expands full-screen into the embedded Manual editor.
  const startManual = (answer: string) => {
    commitAndAdvance(answer, 'manualHandoff');
    setHandingOff(true);
    void (async () => {
      const id = await onCreateGroup?.(values);
      if (id && programFormat) {
        onEnterManual?.({ groupId: id, programFormat, programName: values.name });
      }
    })();
  };

  const handleDesign = async (intent: 'design-now' | 'design-later') => {
    if (submitting) return;
    // The design prompt stays as the active message; its controls swap to a
    // spinner. Submitting transitions away immediately (Design Now navigates to
    // the builder; Design Later closes the drawer), so there's nothing to record.
    setSubmitting(intent);
    try {
      await onSubmit(values, intent);
    } finally {
      setSubmitting(null);
    }
  };

  // ── Free-text validity for the input bar ────────────────────────────────
  const docTextMode = phase === 'elliottDocs' && textMode !== 'idle';
  // Preview feedback: reply bar accepts feedback once a preview is showing.
  const previewFeedbackMode =
    phase === 'elliottPreview' && preview !== null && !generatingPreview && !handingOff;
  const isFreeText = FREE_TEXT_PHASES.includes(phase) || docTextMode || previewFeedbackMode;
  const outcomeLineCount = draft
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean).length;
  const draftValid =
    phase === 'inviteEmail' || phase === 'adminEmail'
      ? EMAIL_RE.test(draft.trim())
      : phase === 'elliottOutcomes'
      ? outcomeLineCount >= 3
      : draft.trim().length > 0;
  const inputPlaceholder = docTextMode
    ? textMode === 'title'
      ? 'Give this text a title...'
      : 'Type or paste the text content...'
    : previewFeedbackMode
    ? 'Give feedback to refine the preview...'
    : 'Reply to Elliott';

  // Audience options grouped by their owning attribute list (e.g. "Department"),
  // keeping the list id so each group can carry its own Any/All.
  const audienceGroups = audienceCatalog
    .map((list) => ({
      id: list._id,
      name: list.name,
      items: list.values
        .filter((v) => v._id && v.value)
        .map((v) => ({ id: String(v._id), label: v.value }))
    }))
    .filter((group) => group.items.length > 0);

  const toggleAudience = (id: string) =>
    setValues((prev) => ({
      ...prev,
      audience: prev.audience.includes(id)
        ? prev.audience.filter((a) => a !== id)
        : [...prev.audience, id]
    }));

  const setAudienceMatch = (listId: string, matchType: AudienceMatchType) =>
    setValues((prev) => ({ ...prev, audienceMatch: { ...prev.audienceMatch, [listId]: matchType } }));

  // The explainer plus one row per attribute list (Any/All toggle + value pills).
  // Shared by the audience step and the Recommended Course review.
  const renderAudienceGroups = () => (
    <>
      <Typography sx={{ ...messageBodySx, fontSize: 12, color: t.textTertiary }}>
        {AUDIENCE_MATCH_EXPLAINER}
      </Typography>
      {audienceGroups.map((group) => {
        const pickedCount = group.items.filter((opt) => values.audience.includes(opt.id)).length;
        return (
          <Box key={group.id} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: t.textTertiary
                }}
              >
                {group.name}
              </Typography>
              <AudienceMatchToggle
                value={values.audienceMatch[group.id] ?? 'any'}
                disabled={pickedCount === 0}
                onChange={(matchType) => setAudienceMatch(group.id, matchType)}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {group.items.map((opt) => (
                <OptionPill
                  key={opt.id}
                  label={opt.label}
                  selected={values.audience.includes(opt.id)}
                  onClick={() => toggleAudience(opt.id)}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </>
  );

  // "Proceed" — continue with the explicitly selected audiences. With the
  // creation-flow revamp they're stored only; invites go out on Invite & Finish.
  // Only available once at least one audience is picked.
  const proceedWithSelected = () => {
    const parts = audienceGroups
      .map((group) => {
        const picked = group.items.filter((opt) => values.audience.includes(opt.id)).map((opt) => opt.label);
        if (picked.length === 0) return null;
        // Any/All only means something with 2+ values picked in the list.
        const match =
          picked.length > 1 ? ` (${(values.audienceMatch[group.id] ?? 'any') === 'all' ? 'All' : 'Any'})` : '';
        return `${group.name}${match}: ${picked.join(', ')}`;
      })
      .filter((part): part is string => Boolean(part));
    if (parts.length === 0) return;
    update('enrollmentMode', 'proceed');
    commitAndAdvance(parts.join(' · '), 'launchDate');
  };

  // "Auto Enroll All" — auto-enrol every learner org-wide; ignores any selection.
  const autoEnrolAll = () => {
    update('audience', []);
    update('enrollmentMode', 'auto-enroll');
    commitAndAdvance('Enroll all learners automatically', 'launchDate');
  };

  // "Skip & Enroll Manually" — no auto-enrol; the admin enrols learners later.
  const skipAudience = () => {
    update('audience', []);
    update('enrollmentMode', 'manual');
    commitAndAdvance('Skip. Enroll manually', 'launchDate');
  };

  const submitLaunchDate = () => {
    const label = values.launchDate ? formatDate(fromEpochSeconds(values.launchDate)) : 'No publish-by date yet';
    commitAndAdvance(label, 'owner');
  };

  // ── Auto-scroll the thread as it grows ──────────────────────────────────
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = window.setTimeout(
      () => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }),
      60
    );
    return () => window.clearTimeout(id);
  }, [thread.length, phase, typing, promptStreamed, values.audience.length, submitting]);

  const fadeInSx = {
    animation: 'addProgFadeIn 0.35s ease',
    '@keyframes addProgFadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1 } }
  } as const;

  // Controls render below the active prompt once it has finished streaming.
  const showControls = !typing && promptStreamed;
  const indentSx = { pl: `${AVATAR_GUTTER}px` };

  // Shared discrete-entry list for Outcomes / Objectives. Min 3 fields (the
  // first three required), + to add more, X to remove extras, per-field char
  // count enforced — matching the standalone AIMD Outcomes screen.
  const renderEntryFields = (
    items: string[],
    setItems: React.Dispatch<React.SetStateAction<string[]>>,
    labelPrefix: string,
    onContinue: (filled: string[]) => void
  ) => {
    const filled = items.map((v) => v.trim()).filter(Boolean);
    const canContinue = filled.length >= MIN_REQUIRED_OUTCOMES;
    return (
      <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: 480, ...fadeInSx }}>
        {items.map((value, index) => {
          const isRequired = index < MIN_REQUIRED_OUTCOMES;
          return (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Box sx={{ flex: 1 }}>
                <DosenInput
                  label={`${labelPrefix} ${index + 1}${isRequired ? '*' : ''}`}
                  value={value}
                  onChange={(e) =>
                    setItems((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
                  }
                  maxChars={OUTCOME_MAX_LENGTH}
                  fullWidth
                />
              </Box>
              {!isRequired && (
                <Box
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                  title="Remove"
                  sx={{
                    mt: '10px',
                    display: 'flex',
                    cursor: 'pointer',
                    color: t.textTertiary,
                    '&:hover': { color: t.error }
                  }}
                >
                  <LuX size={16} />
                </Box>
              )}
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '2px' }}>
          <Box
            onClick={() => setItems((prev) => [...prev, ''])}
            title="Add another"
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: t.textPrimary,
              border: `1px solid ${t.borderContrast}`,
              '&:hover': { borderColor: t.brandLight, color: t.brandLight }
            }}
          >
            <LuPlus size={16} />
          </Box>
          <OptionPill
            label="Continue"
            variant="cta"
            disabled={!canContinue}
            onClick={() => canContinue && onContinue(filled)}
          />
        </Box>
      </Box>
    );
  };

  // Org-member picker used by the owner step and Invite Org Admin.
  const renderMemberSelect = (value: string, onPick: (id: string, name: string) => void) => (
    <Box sx={{ ...indentSx, maxWidth: 320, ...fadeInSx }}>
      <Select
        value={value}
        onChange={(e) => {
          const id = e.target.value as string;
          onPick(id, existingMembers.find((m) => m.id === id)?.name ?? 'Selected person');
        }}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        fullWidth
        renderValue={(val) =>
          val ? (
            existingMembers.find((m) => m.id === val)?.name ?? ''
          ) : (
            <Box component="span" sx={{ color: t.textTertiary }}>
              Select a person...
            </Box>
          )
        }
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.70)',
          borderRadius: '6px',
          color: t.textPrimary,
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.borderContrast },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.textTertiary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.brandLight },
          '& .MuiSelect-select': { py: '8px', px: '12px' },
          '& .MuiSelect-icon': { color: t.textTertiary, right: '12px' }
        }}
        MenuProps={{
          // Drop the list directly below the field and cap its height so a
          // long member list scrolls in place instead of spilling past the
          // modal edges.
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: {
            sx: {
              mt: '4px',
              width: 320,
              maxWidth: 320,
              maxHeight: 240,
              overflowY: 'auto',
              bgcolor: t.surface,
              border: `1px solid ${t.borderDim}`,
              '& .MuiMenuItem-root': {
                color: t.textSecondary,
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                whiteSpace: 'normal'
              }
            }
          }
        }}
      >
        {existingMembers.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            {m.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const renderControls = () => {
    if (!showControls) return null;
    switch (phase) {
      // Recommended Course review — every input Elliott filled in, on one
      // screen, editable, with the generated preview underneath. From here the
      // admin goes straight to Structure rather than back through the
      // step-by-step conversation.
      case 'reviewRecommendation':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '14px', ...fadeInSx }}>
            <ReviewField label="Program name">
              <TextField
                value={values.name}
                onChange={(e) => update('name', e.target.value)}
                size="small"
                fullWidth
                sx={reviewInputSx}
              />
            </ReviewField>

            <ReviewField label="Category">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {CATEGORY_OPTIONS.map((opt) => (
                  <OptionPill
                    key={opt.key}
                    label={opt.label}
                    selected={values.programType === opt.key}
                    onClick={() => update('programType', opt.key)}
                  />
                ))}
              </Box>
            </ReviewField>

            <ReviewField label="Description">
              <TextField
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={3}
                sx={reviewInputSx}
              />
            </ReviewField>

            <ReviewField label="Audience">
              {audienceOptions.length === 0 ? (
                <Typography sx={{ ...messageBodySx, fontSize: 13, color: t.textTertiary }}>
                  No attributes are configured for this organization yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {renderAudienceGroups()}
                </Box>
              )}
            </ReviewField>

            <ReviewField label="Tone">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {toneOptions.map((opt) => {
                  const isSelected = tone.includes(opt.value);
                  const atLimit = tone.length >= 3 && !isSelected;
                  return (
                    <OptionPill
                      key={opt.value}
                      label={opt.label}
                      selected={isSelected}
                      disabled={atLimit}
                      onClick={() =>
                        setTone((prev) =>
                          prev.includes(opt.value)
                            ? prev.filter((v) => v !== opt.value)
                            : prev.length >= 3
                            ? prev
                            : [...prev, opt.value]
                        )
                      }
                    />
                  );
                })}
              </Box>
            </ReviewField>

            <ReviewField label="Knowledge">
              {/* The recommendation names the sources it drew on; the real
                  document picker lives in the Documents step of the wizard. */}
              {(recommendedSeed?.documents ?? []).length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(recommendedSeed?.documents ?? []).map((d) => (
                    <Typography
                      key={d}
                      sx={{
                        fontSize: 12,
                        color: t.textSecondary,
                        border: `1px solid ${t.borderContrast}`,
                        borderRadius: '4px',
                        px: '8px',
                        py: '3px'
                      }}
                    >
                      {d}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ ...messageBodySx, fontSize: 13, color: t.textTertiary }}>
                  I'll ground this in your Knowledge. You can add specific documents at the next step.
                </Typography>
              )}
            </ReviewField>

            <ReviewField label="Preview">
              {generatingPreview || !preview ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CircularProgress size={16} sx={{ color: t.brandLight }} />
                  <Typography sx={{ ...messageBodySx, fontSize: 13, color: t.textSecondary }}>
                    Putting a preview together...
                  </Typography>
                </Box>
              ) : (
                <ParagraphText text={preview} sx={{ fontSize: 13 }} />
              )}
            </ReviewField>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', pt: '4px' }}>
              <OptionPill
                label={handingOff ? 'Building...' : 'Build Structure'}
                variant="cta"
                disabled={handingOff || !values.name.trim() || !values.programType || !groupId}
                onClick={handleElliottHandoff}
              />
              <OptionPill label="Regenerate Preview" disabled={generatingPreview || !groupId} onClick={() => startPreviewGeneration()} />
            </Box>
          </Box>
        );

      case 'category':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {CATEGORY_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.key}
                label={opt.label}
                description={opt.description}
                dotColor={PROGRAM_TYPE_COLORS[opt.key]}
                selected={values.programType === opt.key}
                onClick={() => {
                  update('programType', opt.key);
                  commitAndAdvance(opt.label, 'audience');
                }}
              />
            ))}
          </Box>
        );

      case 'audience':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
            {audienceOptions.length === 0 ? (
              <Typography sx={{ ...messageBodySx, fontSize: 13, color: t.textTertiary }}>
                No attributes are configured for this organization yet.
              </Typography>
            ) : (
              renderAudienceGroups()
            )}
            {/* Subtle separation between the audience options and the actions. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                mt: '6px',
                pt: '14px',
                borderTop: `1px solid ${t.borderDim}`
              }}
            >
              <OptionPill
                label="Proceed"
                variant="cta"
                disabled={values.audience.length === 0}
                onClick={proceedWithSelected}
              />
              <OptionPill label="Auto-Enroll Everyone" onClick={autoEnrolAll} />
              <OptionPill label="Skip and Enroll Manually" onClick={skipAudience} />
            </Box>
          </Box>
        );

      case 'launchDate':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', ...fadeInSx }}>
            {/* Calendar shown inline in the thread — no field to click open. */}
            <Box
              sx={{
                bgcolor: '#151515',
                border: `1px solid ${t.borderContrast}`,
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StaticDatePicker
                  value={values.launchDate ? new Date(values.launchDate * 1000) : null}
                  onChange={(d: Date | null) =>
                    update('launchDate', d ? Math.floor(d.getTime() / 1000) : null)
                  }
                  displayStaticWrapperAs="desktop"
                  slotProps={{ actionBar: { actions: [] } }}
                  sx={{
                    bgcolor: 'transparent',
                    '& .MuiPickersLayout-root, & .MuiPickersLayout-contentWrapper': { bgcolor: 'transparent' },
                    '& .MuiPickersCalendarHeader-label': { color: t.textPrimary, fontFamily: 'Inter, sans-serif' },
                    '& .MuiPickersArrowSwitcher-button': { color: t.textPrimary },
                    '& .MuiDayCalendar-weekDayLabel': { color: t.textSecondary, fontFamily: 'Inter, sans-serif' },
                    '& .MuiPickersDay-root': {
                      color: t.textPrimary,
                      fontFamily: 'Inter, sans-serif',
                      '&:hover': { bgcolor: 'rgba(247,148,32,0.12)' },
                      '&.MuiPickersDay-today': { borderColor: t.borderContrast },
                      '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                        background: t.brandyTextGradient,
                        color: t.textPrimary
                      }
                    },
                    '& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected': {
                      background: t.brandyTextGradient,
                      color: t.textPrimary
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <OptionPill label="Confirm Date" variant="cta" disabled={!values.launchDate} onClick={submitLaunchDate} />
              <OptionPill
                label="Skip For Now"
                onClick={() => {
                  update('launchDate', null);
                  commitAndAdvance('No publish-by date yet', 'owner');
                }}
              />
            </Box>
          </Box>
        );

      case 'owner':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexWrap: 'wrap', gap: '8px', ...fadeInSx }}>
            {me && (
              <OptionPill
                label="Me"
                onClick={() => {
                  update('ownerMode', 'existing');
                  update('existingOwnerId', me.id);
                  commitAndAdvance('Me', 'design');
                }}
              />
            )}
            <OptionPill
              label="Choose Existing Person"
              disabled={existingMembers.length === 0}
              onClick={() => {
                update('ownerMode', 'existing');
                commitAndAdvance('Choose Existing Person', 'ownerExisting');
              }}
            />
            <OptionPill
              label="Invite Someone New"
              onClick={() => {
                update('ownerMode', 'invite');
                commitAndAdvance('Invite Someone New', 'inviteFirst');
              }}
            />
          </Box>
        );

      case 'ownerExisting':
        return renderMemberSelect(values.existingOwnerId ?? '', (id, name) => {
          update('existingOwnerId', id);
          commitAndAdvance(name, 'design');
        });

      case 'adminMode':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexWrap: 'wrap', gap: '8px', ...fadeInSx }}>
            <OptionPill
              label="Choose Existing Person"
              disabled={existingMembers.length === 0}
              onClick={() => commitAndAdvance('Choose Existing Person', 'adminExisting')}
            />
            <OptionPill
              label="Invite Someone New"
              onClick={() => commitAndAdvance('Invite Someone New', 'adminFirst')}
            />
          </Box>
        );

      case 'adminExisting':
        return renderMemberSelect('', (id, name) => {
          setThread((prev) => [
            ...prev,
            { id: `adminExisting-q-${prev.length}`, role: 'elliott', text: activePromptText },
            { id: `adminExisting-a-${prev.length}`, role: 'learner', text: name }
          ]);
          setPromptOverride(`Making ${name} an org admin...`);
          setPhase('adminDone');
          void (async () => {
            const ok = (await onPromoteOrgAdmin?.(id)) ?? false;
            setPromptOverride(
              ok
                ? `${name} is now an Org Admin. They will have admin access across your organization the next time they sign in.`
                : `I couldn't make ${name} an org admin. Please try again, or manage roles from People.`
            );
          })();
        });

      case 'design':
        return submitting ? (
          <Box sx={{ ...indentSx, display: 'flex', alignItems: 'center', gap: '10px', ...fadeInSx }}>
            <CircularProgress size={16} sx={{ color: t.brandLight }} />
            <Typography sx={{ ...messageBodySx, fontSize: 14, color: t.textSecondary }}>
              Setting up your program...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ ...indentSx, display: 'flex', flexWrap: 'wrap', gap: '8px', ...fadeInSx }}>
            <OptionPill
              label="Design Now"
              variant="cta"
              onClick={() => {
                if (!typeFirst) {
                  commitAndAdvance('Design Now', 'programType');
                  return;
                }
                // The type and how to build it were asked first: straight on to the build.
                if (programFormat === 'mentorship') commitAndAdvance('Design Now', 'mentorObjectives');
                else startManual('Design Now');
              }}
            />
            <OptionPill label="Design Later" onClick={() => handleDesign('design-later')} />
          </Box>
        );

      case 'menu':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {onBuildFramework && (
              <OptionCard
                label={frameworkBuilt ? 'See my Program Plan' : 'Build my Program Plan'}
                description={
                  frameworkBuilt
                    ? `Review the Assessments and Training I recommend for ${orgPoss}, and add them to the Dashboard whenever you're ready.`
                    : `Answer a few questions and I'll recommend the programs ${orgPoss} people need.`
                }
                onClick={() => onBuildFramework()}
              />
            )}
            {/* Identify Knowledge Gaps & Risks used to sit here. Gaps now live
                permanently on the dashboard's own panel, so the menu no longer
                offers a way in. */}
            {/* Setup Guide — the setup counter moved here from the dashboard's
                gamification bar; the badge shows steps done. */}
            {setupProgress && (
              <OptionCard
                label="Setup Guide"
                description={`Finish setting up ${orgPoss} learning with me, step by step.`}
                badge={`${setupProgress.done}/${setupProgress.total}`}
                onClick={() => onOpenSetupGuide?.()}
              />
            )}
            {/* Sync Knowledge is only useful before a knowledge base is
                connected — once one exists, the action drops off the menu. */}
            {!hasKnowledgeIntegration && (
              <OptionCard
                label="Sync Knowledge"
                description={`Ensure I have the latest source of truth when powering ${orgPoss} learning.`}
                onClick={handleSyncKnowledge}
              />
            )}
            {/* Add New Program is always offered, on the first visit and on
                every return to the menu. */}
            <OptionCard
              label="Build a program"
              description="Design and publish a new learning program with me, step by step."
              onClick={() => commitAndAdvance('Build a program', 'welcome')}
            />
            {canInviteOrgAdmin && (
              <OptionCard
                label="Invite Org Admin"
                description={`Bring another org admin on board to help power ${orgPoss} learning.`}
                onClick={() => commitAndAdvance('Invite Org Admin', 'adminMode')}
              />
            )}
            {isOrgAdmin && (
              <OptionCard
                label="Edit Stages"
                description={`Match the stages on the Dashboard to how ${orgLabel} works.`}
                onClick={() => onEditFramework?.()}
              />
            )}
            <OptionCard
              label="Generate Report"
              description="I'll build instant, custom reports on your programs and learners."
              badge="Coming Soon"
              comingSoon
            />
            <OptionCard
              label="My Tasks"
              description="Automated task suggestions to drive your learners forward."
              badge="Coming Soon"
              comingSoon
            />
          </Box>
        );


      case 'pillar':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {pillars.map((p) => (
              <OptionCard
                key={p.id}
                label={p.name}
                onClick={() => {
                  onPillarSelect?.(p.id);
                  commitAndAdvance(p.name, 'name');
                }}
              />
            ))}
          </Box>
        );

      case 'programType':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {programTypeOptions(w).map((opt) => (
              <OptionCard
                key={opt.key}
                label={opt.label}
                description={opt.description}
                selected={programFormat === opt.key}
                onClick={() => {
                  setProgramFormat(opt.key);
                  if (typeFirst) {
                    // Asked first: a Mentorship goes on to its details; a course, to how it is built.
                    commitAndAdvance(opt.label, opt.key === 'mentorship' ? detailsStart : 'buildMethod');
                    return;
                  }
                  // Mentorship is single-path — skip build method, capture its
                  // objectives + match settings, then hand off to Members.
                  commitAndAdvance(opt.label, opt.key === 'mentorship' ? 'mentorObjectives' : 'buildMethod');
                }}
              />
            ))}
          </Box>
        );

      case 'buildMethod':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {BUILD_METHOD_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.key}
                label={opt.label}
                description={opt.description}
                badge={opt.badge}
                comingSoon={opt.comingSoon}
                selected={buildMethod === opt.key}
                onClick={() => {
                  setBuildMethod(opt.key);
                  if (typeFirst && opt.key === 'elliott' && programFormat && programFormat !== 'mentorship') {
                    // Built in Publish Training, which asks for everything else itself.
                    onBuildWithElliott?.(programFormat);
                    return;
                  }
                  if (typeFirst && opt.key === 'manual') {
                    // A manual build: its details are captured here, then the editor opens.
                    commitAndAdvance(opt.label, detailsStart);
                    return;
                  }
                  if (opt.key === 'elliott') {
                    commitAndAdvance(opt.label, 'elliottPurpose');
                    // Create the group now so the Documents step can upload to it.
                    void (async () => {
                      const id = await onCreateGroup?.(values);
                      if (id) setGroupId(id);
                    })();
                  } else if (opt.key === 'manual') {
                    startManual(opt.label);
                  } else {
                    commitAndAdvance(opt.label, 'wip');
                  }
                }}
              />
            ))}
          </Box>
        );

      case 'elliottTone':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            {toneOptions.map((opt) => {
              const isSelected = tone.includes(opt.value);
              const atLimit = tone.length >= 3 && !isSelected;
              return (
                <OptionCard
                  key={opt.value}
                  label={opt.label}
                  description={opt.description}
                  selected={isSelected}
                  disabled={atLimit}
                  onClick={() =>
                    setTone((prev) =>
                      prev.includes(opt.value)
                        ? prev.filter((v) => v !== opt.value)
                        : prev.length < 3
                        ? [...prev, opt.value]
                        : prev
                    )
                  }
                />
              );
            })}
            <Box sx={{ display: 'flex', mt: '4px' }}>
              <OptionPill
                label="Continue"
                variant="cta"
                disabled={tone.length === 0}
                onClick={() =>
                  commitAndAdvance(
                    tone
                      .map((v) => toneOptions.find((o) => o.value === v)?.label ?? v)
                      .join(', '),
                    'elliottOutcomes'
                  )
                }
              />
            </Box>
          </Box>
        );

      case 'elliottDocs':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '14px', ...fadeInSx }}>
            {/* Existing knowledge — shown first, above the upload options. */}
            {documents.previousDocuments.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.textSecondary }}>
                  Select from existing knowledge
                </Typography>
                {(prevExpanded
                  ? documents.previousDocuments
                  : documents.previousDocuments.slice(0, 3)
                ).map((d) => (
                  <DocRow
                    key={d.id}
                    doc={d}
                    onToggle={() => toggleDoc(d.id)}
                    onInstruction={(v) => setDocInstruction(d.id, v)}
                  />
                ))}
                {documents.previousDocuments.length > 3 && (
                  <Box sx={{ display: 'flex' }}>
                    <OptionPill
                      label={prevExpanded ? 'Show Less' : `View All (${documents.previousDocuments.length})`}
                      onClick={() => setPrevExpanded((e) => !e)}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Upload new — three options, same card design as Tone / Build Method. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.textSecondary }}>
                Or upload new knowledge now
              </Typography>
              <OptionCard
                label="Upload Files"
                description="Add PDFs, docs or slides from your computer."
                onClick={() => fileInputRef.current?.click()}
              />
              <OptionCard
                label="Upload Text"
                description="Type or paste content straight into the chat."
                onClick={() => {
                  setUrlMode(false);
                  setTextMode('title');
                }}
              />
              <OptionCard
                label="Add URL"
                description="Point Elliott at a web page or public Notion page to learn from."
                onClick={() => {
                  setTextMode('idle');
                  setUrlMode(true);
                }}
              />
              <Box component="input" type="file" multiple hidden ref={fileInputRef} onChange={handleFilePick} />
            </Box>

            {/* Inline Add-URL form */}
            {urlMode && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  p: '12px',
                  borderRadius: '6px',
                  border: `1px solid ${t.borderContrast}`,
                  bgcolor: t.surface
                }}
              >
                {[
                  { ph: 'Title (optional)', val: urlTitle, set: setUrlTitle },
                  { ph: 'https://...', val: urlValue, set: setUrlValue }
                ].map((f, i) => (
                  <TextField
                    key={i}
                    value={f.val}
                    onChange={(e) => f.set(e.target.value)}
                    placeholder={f.ph}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(0,0,0,0.4)',
                        borderRadius: '6px',
                        color: t.textPrimary,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        '& fieldset': { borderColor: t.borderContrast },
                        '&:hover fieldset': { borderColor: t.textTertiary },
                        '&.Mui-focused fieldset': { borderColor: t.brandLight }
                      },
                      '& .MuiOutlinedInput-input::placeholder': { color: t.textTertiary, opacity: 1 }
                    }}
                  />
                ))}
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <OptionPill label="Add URL" variant="cta" disabled={!urlValue.trim()} onClick={submitUrl} />
                  <OptionPill
                    label="Cancel"
                    onClick={() => {
                      setUrlMode(false);
                      setUrlTitle('');
                      setUrlValue('');
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Uploaded + in-flight documents */}
            {(pendingFiles.length > 0 || documents.uploadedDocuments.length > 0) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {pendingFiles.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      px: '12px',
                      py: '10px',
                      borderRadius: '6px',
                      border: `1px solid ${t.borderContrast}`,
                      bgcolor: t.surface
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: t.brandLight }} />
                    <Typography sx={{ fontSize: 13, color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Uploading {p.name}...
                    </Typography>
                  </Box>
                ))}
                {documents.uploadedDocuments.map((d) => (
                  <DocRow
                    key={d.id}
                    doc={d}
                    onToggle={() => toggleDoc(d.id)}
                    onInstruction={(v) => setDocInstruction(d.id, v)}
                    onRemove={() => removeUploadedDoc(d.id)}
                  />
                ))}
              </Box>
            )}

            {/* Upload-Text prompt — shown as an Elliott message after any uploads;
                the title then the body go into the reply bar (no Continue CTA). */}
            {textMode !== 'idle' && (
              <Box sx={{ ml: `-${AVATAR_GUTTER}px` }}>
                <ElliottMessage>
                  <Typography sx={messageBodySx}>
                    {textMode === 'title'
                      ? 'Type a title for your text in the reply bar below and press enter.'
                      : `“${textTitle}”: now type or paste the content in the reply bar below.`}
                  </Typography>
                </ElliottMessage>
              </Box>
            )}

            {/* Continue / Skip — hidden while adding text (title/body use Enter).
                Continue waits while a ticked document is still indexing. */}
            {textMode === 'idle' && (
            <Box sx={{ display: 'flex', gap: '8px', mt: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
              <OptionPill
                label={selectedDocIds.length > 0 ? 'Continue' : 'Skip'}
                variant="cta"
                disabled={blockedSelectedCount > 0}
                onClick={() =>
                  commitAndAdvance(
                    selectedDocIds.length > 0
                      ? `${selectedDocIds.length} document(s) selected`
                      : 'No documents',
                    'elliottPreview'
                  )
                }
              />
              {blockedSelectedCount > 0 && (
                <Typography sx={{ fontSize: 12, color: t.textTertiary }}>
                  {indexingSelectedCount > 0 &&
                    `Waiting for ${indexingSelectedCount} selected document${
                      indexingSelectedCount === 1 ? '' : 's'
                    } to finish indexing`}
                  {indexingSelectedCount > 0 && failedSelectedCount > 0 && '; '}
                  {failedSelectedCount > 0 &&
                    `${failedSelectedCount} selected document${failedSelectedCount === 1 ? '' : 's'} failed to index`}
                    {'. Untick to continue without.'}
                </Typography>
              )}
            </Box>
            )}
          </Box>
        );

      case 'elliottPreview':
        if (generatingPreview || preview === null) {
          return (
            <Box sx={{ ...indentSx, ...fadeInSx }}>
              <TypingDots />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
            <Box sx={{ ...indentSx }}>
              <Box sx={{ p: '14px', borderRadius: '8px', border: `1px solid ${t.borderContrast}`, bgcolor: t.surface }}>
                <ParagraphText text={preview} sx={{ fontSize: 14 }} />
              </Box>
            </Box>
            {/* Feedback invitation as a follow-up Elliott message. */}
            <ElliottMessage>
              <Typography sx={messageBodySx}>
                Tell me what to change in the reply bar below and I’ll update the preview. When the preview reads right,
                we can move on.
              </Typography>
            </ElliottMessage>
            <Box sx={{ ...indentSx }}>
              {handingOff ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CircularProgress size={16} sx={{ color: t.brandLight }} />
                  <Typography sx={{ ...messageBodySx, fontSize: 14, color: t.textSecondary }}>
                    Taking you to your Program Structure...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <OptionPill label="Build Program Structure" variant="cta" onClick={handleElliottHandoff} />
                </Box>
              )}
            </Box>
          </Box>
        );

      case 'elliottOutcomes':
        return renderEntryFields(outcomes, setOutcomes, 'Goal', (filled) => {
          setOutcomes(filled);
          commitAndAdvance(filled.join(' · '), 'elliottDocs');
        });

      case 'mentorObjectives':
        return renderEntryFields(mentorObjectives, setMentorObjectives, 'Objective', (filled) => {
          setMentorObjectives(filled);
          commitAndAdvance(filled.join(' · '), 'mentorMatches');
        });

      case 'mentorMatches':
        return (
          <Box sx={{ ...indentSx, display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
            {[
              {
                key: 'mentee',
                label: `Max matches per ${w('mentee', { lower: true })}`,
                val: matchMentee,
                set: setMatchMentee
              },
              {
                key: 'mentor',
                label: `Max matches per ${w('mentor', { lower: true })}`,
                val: matchMentor,
                set: setMatchMentor
              },
              { key: 'sessions', label: 'Required sessions per match', val: sessionsPerMatch, set: setSessionsPerMatch }
            ].map((f) => (
              <Box
                key={f.key}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', maxWidth: 360 }}
              >
                <Typography sx={{ fontSize: 14, color: t.textSecondary }}>{f.label}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={f.val}
                  onChange={(e) => f.set(Math.max(1, Number(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  sx={{
                    width: 84,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(0,0,0,0.4)',
                      borderRadius: '6px',
                      color: t.textPrimary,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 14,
                      '& fieldset': { borderColor: t.borderContrast },
                      '&:hover fieldset': { borderColor: t.textTertiary },
                      '&.Mui-focused fieldset': { borderColor: t.brandLight }
                    }
                  }}
                />
              </Box>
            ))}
            <Box sx={{ display: 'flex', mt: '2px' }}>
              <OptionPill label="Build Program" variant="cta" onClick={handleEnterMentorship} />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Scrollable conversation thread — content centred + width-capped so lines
          stay readable in the wider central modal. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '24px', py: '24px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', width: '100%', maxWidth: 640, mx: 'auto' }}>
          {/* History */}
          {thread.map((turn) =>
            turn.role === 'elliott' ? (
              <ElliottMessage key={turn.id}>
                <ParagraphText text={turn.text} />
              </ElliottMessage>
            ) : (
              <LearnerMessage key={turn.id}>{turn.text}</LearnerMessage>
            )
          )}

          {/* Active prompt — typing dots, then streaming copy */}
          <ElliottMessage>
            {typing ? (
              <TypingDots />
            ) : (
              <StreamingText
                key={`${phase}-${promptOverride ?? ''}`}
                paragraphs={[activePromptText]}
                sx={messageBodySx}
                onDone={() => {
                  if (phase === 'welcome') {
                    // Welcome has no control — drop it into history, then ask for the
                    // pillar (when opened from the chat with no pillar pre-selected)
                    // before the name.
                    setThread((prev) => [...prev, { id: 'welcome', role: 'elliott', text: activePromptText }]);
                    setPhase(typeFirst ? 'programType' : detailsStart);
                  } else {
                    setPromptStreamed(true);
                    // Preview "generates" once Elliott's lead-in has finished.
                    if (phase === 'elliottPreview' && preview === null) {
                      startPreviewGeneration(lastFeedback || undefined);
                    }
                  }
                }}
              />
            )}
          </ElliottMessage>

          {renderControls()}

          <Box ref={endRef} />
        </Box>
      </Box>

      {/* Sticky reply bar — active only on free-text steps */}
      <Box sx={{ flexShrink: 0, px: '24px', py: '16px', borderTop: `1px solid ${t.borderDim}` }}>
        <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto' }}>
          <ChatInputBar
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            canSend={isFreeText && draftValid && !typing && submitting === null}
            readOnly={!isFreeText || submitting !== null}
            placeholder={inputPlaceholder}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProgramChat;
